import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Button, MenuItem, Stack } from "@mui/material";
import React, { useMemo, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { cacheService, dataService } from "../../../Core/Api/services";
import {
  AssetLiquidExportFileType,
  AssetLiquidExportChilMode,
  DbDeliveryMetadataExportInfos,
} from "../../../Core/Api/Types/db-delivery-metadata-export-infos";
import { DeliveryMetadataExportOrigin } from "../../../Core/Api/Types/delivery-metadata-export-origin";
import { DeliveryMetadataExportType } from "../../../Core/Api/Types/delivery-metadata-export-type";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppSelector } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { useTranslation } from "react-i18next";

export type ModalCreateOrUpdateExportSessionElementProps = {
  mode: "create" | "update";
  object?: DbDeliveryMetadataExportInfos;
};

export type ModalCreateOrUpdateExportSessionProps = CommonModalProps<ModalCreateOrUpdateExportSessionElementProps>;

const ModalCreateOrUpdateExportSession = React.forwardRef<any, ModalCreateOrUpdateExportSessionProps>((props, ref) => {
  const { mode, object, closeModal } = props;
  const { t } = useTranslation();

  const enqueueSnackbar = useCustomSnackbar();

  const formId = useMemo(() => "modal-create-or-update-export-session", []);
  const isFormValid = useAppSelector(GetFormIsValid(formId));

  const user = useMemo(() => cacheService.GetUserInfo(), []);

  const [name, setName] = useState(object?.name || "");
  const [tag, setTag] = useState(object?.tag || "");
  const [description, setDescription] = useState(object?.description || "");
  const [type, setType] = useState<DeliveryMetadataExportType | null>(object?.type || null);
  const [origin, setOrigin] = useState<DeliveryMetadataExportOrigin | null>(object?.origin || null);
  const [xslFilePath, setXslFilePath] = useState(object?.xslFilePath || "");
  const [liquidExportFileType, setLiquidExportFileType] = useState<AssetLiquidExportFileType>(
    object?.liquidAssetExportFileType || AssetLiquidExportFileType.Xml
  );
  const [liquidExportChildMode, setLiquidExportChildMode] = useState<AssetLiquidExportChilMode>(
    object?.liquidAssetExportChildMode || AssetLiquidExportChilMode.None
  );

  const handleCancelClick = () => {
    closeModal && closeModal();
  };

  const handleConfirmClick = () => {
    if (mode === "create")
      dataService
        .CreateMetadataExport({
          id: -500,
          name: name,
          tag: tag,
          description: description,
          type: type,
          origin: origin,
          xslFilePath: xslFilePath,
          isNew: true,
          platformId: user!.platformId,
          template: null,
          liquidAssetExportFileType: liquidExportFileType,
          liquidAssetExportChildMode: liquidExportChildMode,
        })
        .then((response) => {
          if (response && response.success) enqueueSnackbar(t("alert:success.metadataExportCreated"), "success");
          else enqueueSnackbar(t("alert:error.errorCreatingMetadataExport", { message: response.message }), "error");
        });
    else if (object)
      dataService
        .UpdateMetadataExport({
          ...object,
          name,
          tag,
          description,
          type,
          origin,
          xslFilePath,
          liquidAssetExportFileType: liquidExportFileType,
          liquidAssetExportChildMode: liquidExportChildMode,
        })
        .then((response) => {
          if (response && response.success) enqueueSnackbar(t("alert:success.metaDataExportUpdated"), "success");
          else enqueueSnackbar(t("alert:error.errorUpdatingMetadataExport", { message: response.message }), "error");
        });
    closeModal && closeModal();
  };

  return (
    <Modal
      title={mode === "create" ? t("modal:title.createNewExport") : t("modal:title.updateExport", { id: object?.id })}
      reference={ref}
      closeModal={handleCancelClick}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!isFormValid}>
            {mode === "create" ? t("common:create") : t("common:update")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1}>
        <FormInputText
          label={t("common:name")}
          placeholder={t("field:label.exportName")}
          validationProps={{
            formId: formId,
            fieldId: "name",
            required: true,
          }}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <FormInputText
          label={t("common:tag")}
          placeholder={t("field:placeholder.exportTag")}
          validationProps={{
            formId: formId,
            fieldId: "tag",
            required: false,
          }}
          value={tag}
          onChange={(event) => setTag(event.target.value)}
        />
        <FormInputText
          label={t("common:description")}
          placeholder={t("field:placeholder.exportDescription")}
          validationProps={{
            formId: formId,
            fieldId: "description",
            required: true,
          }}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <FormInputSelect
          label={t("common:type")}
          displayEmpty
          validationProps={{
            formId: formId,
            fieldId: "type",
            required: true,
            initialState: type !== null,
          }}
          stackProps={{}}
          value={type}
          onChange={(event) => setType(event.target.value as DeliveryMetadataExportType)}
        >
          {Object.keys(DeliveryMetadataExportType).map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </FormInputSelect>
        <FormInputSelect
          label={t("common:origin")}
          displayEmpty
          validationProps={{
            formId: formId,
            fieldId: "origin",
            required: true,
            initialState: type !== null,
          }}
          stackProps={{}}
          value={origin}
          onChange={(event) => setOrigin(event.target.value as DeliveryMetadataExportOrigin)}
        >
          {Object.keys(DeliveryMetadataExportOrigin).map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
        </FormInputSelect>
        {type !== DeliveryMetadataExportType.LiquidTransform && (
          <FormInputText
            label={t("common:xsltOrExcelPath")}
            placeholder={t("field:placeholder.xsltOrExcelPath")}
            validationProps={{
              formId: formId,
              fieldId: "xslPath",
              required: true,
            }}
            value={xslFilePath}
            onChange={(event) => setXslFilePath(event.target.value)}
          />
        )}
        {(type === DeliveryMetadataExportType.LiquidTransform || type === DeliveryMetadataExportType.ExcelPatcher) && (
          <Stack spacing={1}>
            <FormInputSelect
              label={t("field:label.outputFileTypes")}
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              stackProps={{}}
              value={liquidExportFileType}
              onChange={(e) => setLiquidExportFileType(e.target.value as AssetLiquidExportFileType)}
              validationProps={{
                formId: formId,
                fieldId: "fileType",
                required: true,
                initialValue: liquidExportFileType,
              }}
            >
              {Object.keys(AssetLiquidExportFileType).map((fileType) => (
                <MenuItem key={fileType} value={fileType}>
                  {fileType}
                </MenuItem>
              ))}
            </FormInputSelect>
            <FormInputSelect
              label={t("field:label.childMode")}
              IconComponent={ExpandMoreRoundedIcon}
              inputProps={{ "aria-label": "Without label" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              stackProps={{}}
              value={liquidExportChildMode}
              onChange={(e) => setLiquidExportChildMode(e.target.value as AssetLiquidExportChilMode)}
              validationProps={{
                formId: formId,
                fieldId: "childMode",
                required: true,
                initialValue: liquidExportChildMode,
              }}
            >
              {Object.keys(AssetLiquidExportChilMode).map((fileType) => (
                <MenuItem key={fileType} value={fileType}>
                  {fileType}
                </MenuItem>
              ))}
            </FormInputSelect>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
});

export default ModalCreateOrUpdateExportSession;
